// covered-by: packages/schemas/src/christo/__tests__/askPunttHomeCopy.test.ts
/**
 * AskPuntt V2 home copy contract.
 *
 * The home page keeps the translated "Ask Puntt" headline immediately and
 * skeletons the one get-started line above the suggestion chips until
 * christo returns a personalized version written by one LLM call over the
 * user, their enterprise, and their local time. `copy` is null whenever
 * personalization did not happen (producer down, missing config, model
 * error) — the FE then keeps the translated defaults. `accentWord` is the
 * one headline word to paint in the brand blue; it must be a whole word from
 * `headline`.
 */
import { z } from "zod";

export const ASK_PUNTT_HOME_COPY_LIMITS = {
  headline: 60,
  subheading: 90,
  accentWord: 32,
} as const;

export const askPunttHomeCopySchema = z.object({
  headline: z.string().min(1).max(ASK_PUNTT_HOME_COPY_LIMITS.headline),
  subheading: z.string().min(1).max(ASK_PUNTT_HOME_COPY_LIMITS.subheading),
  accentWord: z
    .string()
    .min(1)
    .max(ASK_PUNTT_HOME_COPY_LIMITS.accentWord)
    .optional(),
});
export type AskPunttHomeCopy = z.infer<typeof askPunttHomeCopySchema>;

export type HeadlineAccentSplit = {
  before: string;
  accent: string;
  after: string;
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** First whole-word match of `word` in `haystack`, or null. */
export function findWholeWordInHeadline(
  haystack: string,
  word: string,
): string | null {
  const needle = word.trim();
  if (!needle || /\s/.test(needle)) return null;
  const match = new RegExp(
    `(^|[^A-Za-z0-9])(${escapeRegExp(needle)})(?![A-Za-z0-9])`,
  ).exec(haystack);
  return match?.[2] ?? null;
}

/**
 * The word Carrie should paint blue. Prefers the model's designation when
 * it is a whole word in the headline; otherwise "Puntt" when present.
 */
export function resolveHomeCopyAccentWord(
  headline: string,
  designated?: string | null,
): string | undefined {
  const cleaned = (designated ?? "").trim().replace(/[.,!?;:]+$/g, "");
  const designatedMatch = findWholeWordInHeadline(headline, cleaned);
  if (designatedMatch) return designatedMatch;
  return findWholeWordInHeadline(headline, "Puntt") ?? undefined;
}

/** Split a headline around the resolved accent word. Null when none match. */
export function splitHeadlineOnAccent(
  headline: string,
  accentWord?: string | null,
): HeadlineAccentSplit | null {
  const accent = resolveHomeCopyAccentWord(headline, accentWord);
  if (!accent) return null;
  const match = new RegExp(
    `(^|[^A-Za-z0-9])(${escapeRegExp(accent)})(?![A-Za-z0-9])`,
  ).exec(headline);
  if (!match || match.index == null) return null;
  const start = match.index + match[1].length;
  return {
    before: headline.slice(0, start),
    accent: match[2],
    after: headline.slice(start + match[2].length),
  };
}

/**
 * Client clock, sent verbatim so the server can describe the user's day
 * without guessing their zone. `localDateTime` is the wall-clock time in
 * `timezone`, ISO-shaped and second-precision optional: `2026-09-16T22:06`.
 */
export const askPunttHomeCopyClientTimeSchema = z.object({
  timezone: z.string().min(1).max(64),
  localDateTime: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/,
      "localDateTime must look like YYYY-MM-DDTHH:mm",
    ),
  locale: z.string().min(2).max(16).optional(),
});
export type AskPunttHomeCopyClientTime = z.infer<
  typeof askPunttHomeCopyClientTimeSchema
>;

export const getAskPunttHomeCopyRequestSchema = z.object({
  body: askPunttHomeCopyClientTimeSchema,
});

export const getAskPunttHomeCopyResponseSchema = z.object({
  copy: askPunttHomeCopySchema.nullable(),
  personalized: z.boolean(),
  generatedAt: z.string(),
});
export type GetAskPunttHomeCopyResponse = z.infer<
  typeof getAskPunttHomeCopyResponseSchema
>;
